// 导入
import { getPlatformFieldMap } from '@/config/tabConfig'

// localStorage key
const localStorageKey = 'favoriteProducts'
const localStorageKeyUser = 'favorites_user_v2'

// 内存缓存（用于列表页面批量同步）
let cache = {}

// hashCode函数（与后端一致）
const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// 生成稳定的设备指纹作为游客ID（不依赖存储，清缓存也不变）
const generateGuestId = () => {
  // Web端指纹：使用浏览器特征组合
  const webFeatures = [
    navigator.userAgent || '',
    (screen.width || '0') + 'x' + (screen.height || '0'),
    new Date().getTimezoneOffset() || '0',
    navigator.language || '',
    navigator.hardwareConcurrency || '0'
  ].join('|');

  return 'fp_' + hashCode(webFeatures);
}

const favoriteManager = {
  // 初始化
  init(_userId) {
    // 从 localStorage 加载缓存
    this.loadFromLocalStorage()
    // 同时初始化 window.favoriteCache（兼容 main.js 的初始化逻辑）
    if (!window.favoriteCache) {
      window.favoriteCache = new Map()
    }
    // 从 favoriteManager 的缓存同步到 window.favoriteCache
    Object.entries(cache).forEach(([key, value]) => {
      if (value?.isFavorite) {
        window.favoriteCache.set(key, true)
      }
    })
    // console.log('[favoriteManager] 同步缓存到 window.favoriteCache:', window.favoriteCache.size, '条')
  },

  // 获取游客ID（使用设备指纹，稳定可靠）
  getGuestId() {
    // 直接用设备指纹生成，不依赖localStorage
    const guestId = generateGuestId();
    // console.log('[favoriteManager] guestId(fingerprint):', guestId);
    return guestId;
  },

  // 加载本地存储的收藏数据到内存
  loadFromLocalStorage() {
    try {
      // 加载本地收藏数据（与保存时使用相同的key）
      const userData = localStorage.getItem(localStorageKeyUser)
      const favData = localStorage.getItem(localStorageKey)
      // console.log('[favoriteManager] loadFromLocalStorage - userData存在:', !!userData, 'favData存在:', !!favData)
      if (userData) {
        cache = JSON.parse(userData)
        // console.log('[favoriteManager] 从localStorage加载缓存:', Object.keys(cache).length, '条')
      } else if (favData) {
        // 兼容：如果没有user数据但有fav数据，也加载
        cache = JSON.parse(favData)
        // console.log('[favoriteManager] 从备用key加载缓存:', Object.keys(cache).length, '条')
      }
    } catch (e) {
      console.error('[favoriteManager] loadFromLocalStorage 失败:', e)
    }
  },

  // 获取本地收藏列表（所有用户通用）
  getLocalFavorites() {
    try {
      const data = localStorage.getItem(localStorageKey)
      if (data) {
        return JSON.parse(data)
      }
    } catch (e) {
      console.error('[favoriteManager] getLocalFavorites 失败:', e)
    }
    return {}
  },

  // 检查是否已收藏（支持单商品查询和批量查询）
  isFavorite(productId, platform) {
    if (!productId) return false
    // 从缓存检查
    if (cache) {
      const key = `${platform}_${productId}`
      if (cache[key]?.isFavorite) return true
    }
    // 从 localStorage 兼容检查（未登录用户）
    const favorites = this.getLocalFavorites()
    const key = `${platform}_${productId}`
    return !!favorites[key]?.isFavorite
  },

  // 批量检查收藏状态
  batchCheckFavorite(productIds, platform) {
    const result = {}
    for (const productId of productIds) {
      result[productId] = this.isFavorite(productId, platform)
    }
    return result
  },

  // 获取商品信息（统一入口）
  getItemInfo(productId, platform) {
    if (!productId) return null
    // 优先从缓存获取
    if (cache) {
      const key = `${platform}_${productId}`
      if (cache[key]) return cache[key]
    }
    // 兜底：从 localStorage 获取（兼容未登录用户）
    const favorites = this.getLocalFavorites()
    const key = `${platform}_${productId}`
    return favorites[key] || null
  },

  // 获取本地存储的所有收藏（用于已登录用户列表展示）
  getLocalFavoritesAll() {
    // 返回本地存储的用户收藏列表
    return this.getLocalFavorites()
  },

  // 批量添加收藏（用于同步后端数据）
  async batchAdd(favorites) {
    if (!Array.isArray(favorites) || favorites.length === 0) return
    try {
      const localFavorites = this.getLocalFavorites()
      for (const item of favorites) {
        const key = `${item.platform}_${item.sku_id || item.productId}`
        localFavorites[key] = {
          productId: item.sku_id || item.productId,
          platform: item.platform,
          isFavorite: true,
          addTime: item.create_time || Date.now(),
          goods_name: item.goods_name || '',
          goods_img: item.goods_img || '',
          price: item.price || 0,
          detail_data: item.detail_data ? (typeof item.detail_data === 'string' ? JSON.parse(item.detail_data) : item.detail_data) : null
        }
      }
      localStorage.setItem(localStorageKey, JSON.stringify(localFavorites))
    } catch (e) {
      console.error('[favoriteManager] batchAdd 失败:', e)
    }
  },

  // 检查本地收藏状态
  checkLocalFavorite(productId, platform) {
    const key = `${this.normalizePlatform(platform)}_${productId}`
    const favorites = this.getLocalFavorites()
    return !!favorites[key]?.isFavorite
  },

  // 获取缓存 key
  getCacheKey(productId, platform) {
    return `${this.normalizePlatform(platform)}_${productId}`
  },

  // 获取缓存状态
  getCacheStatus(productId, platform) {
    const key = `${platform}_${productId}`;
    return cache[key]?.isFavorite;
  },

  // 监听器
  _listeners: [],

  // 订阅收藏状态变化
  subscribe(callback) {
    this._listeners.push(callback)
    return () => {
      this._listeners = this._listeners.filter(cb => cb !== callback)
    }
  },

  // 通知收藏状态变化
  _notify(productId, platform, isFavorite) {
    this._listeners.forEach(cb => cb(productId, platform, isFavorite))
  },

  // 派发收藏事件（兼容旧代码）
  dispatchFavoriteEvent(eventName, data) {
    if (eventName === 'favoriteUpdated') {
      this._notify(data.skuId, data.platform, data.isFavorite)
      // 派发 window 事件，让 FavoriteList 等组件能够监听
      window.dispatchEvent(new CustomEvent('favoriteUpdated', {
        detail: {
          skuId: data.skuId,
          platform: data.platform,
          isFavorite: data.isFavorite,
          productInfo: data.productInfo
        }
      }))
    }
  },

  // 移除指定条件的收藏（统一入口，用于处理后端返回的失效数据）
  removeWhere(condition) {
    const favorites = this.getLocalFavorites()
    const keys = Object.keys(favorites)
    let removedCount = 0

    keys.forEach(key => {
      if (condition(favorites[key])) {
        delete favorites[key]
        removedCount++
      }
    })

    if (removedCount > 0) {
      localStorage.setItem(localStorageKey, JSON.stringify(favorites))
    }

    return removedCount
  },

  // 根据 skuId 移除收藏
  removeBySkuId(skuId, platform) {
    return this.removeWhere(item => item.productId === skuId && item.platform === platform)
  },

  // 根据平台移除收藏
  removeByPlatform(platform) {
    return this.removeWhere(item => item.platform === platform)
  },

  // 移除所有收藏
  removeAll() {
    localStorage.removeItem(localStorageKey)
    cache = {}
  },

  // 更新单个商品收藏状态
  async setFavorite(productId, platform, isFavorite, baseInfo = null, detailData = null) {
    if (!productId) {
      console.warn('[favoriteManager] setFavorite: productId 为空')
      return false
    }
    const key = `${platform}_${productId}`
    try {
      const favorites = this.getLocalFavorites()
      if (isFavorite) {
        favorites[key] = {
          productId,
          platform,
          isFavorite: true,
          addTime: Date.now(),
          goods_name: baseInfo?.goods_name || '',
          goods_img: baseInfo?.goods_img || '',
          price: baseInfo?.price || 0,
          detail_data: detailData  // 存储完整商品详情 (统一用下划线命名)
        }
      } else {
        delete favorites[key]
      }
      localStorage.setItem(localStorageKey, JSON.stringify(favorites))
      // 更新内存缓存
      if (cache) {
        this.updateCache(productId, platform, isFavorite, baseInfo, detailData)
      }
      // 通知变化
      this._notify(productId, platform, isFavorite)
      return true
    } catch (e) {
      console.error('[favoriteManager] setFavorite 失败:', e)
      return false
    }
  },

  // 标记已加载后端数据（避免覆盖本地数据）
  markLoaded() {
    try {
      localStorage.setItem('favorites_loaded', 'true')
    } catch (e) {
      console.error('[favoriteManager] markLoaded 失败:', e)
    }
  },

  // 检查是否已加载后端数据
  isLoaded() {
    try {
      return localStorage.getItem('favorites_loaded') === 'true'
    } catch (e) {
      return false
    }
  },

  // 更新本地存储的收藏状态
  updateLocalStorage(productId, platform, isFavorite, baseInfo = null, detailData = null) {
    try {
      const favorites = this.getLocalFavorites()
      const key = `${this.normalizePlatform(platform)}_${productId}`
      if (isFavorite) {
        favorites[key] = {
          platform,
          productId,
          skuId: productId,  // 兼容 FavoriteList.vue 的格式
          isFavorite: true,  // 标记为已收藏
          addTime: Date.now(),
          goods_name: baseInfo?.goods_name || '',
          goods_img: baseInfo?.goods_img || '',
          price: baseInfo?.price || baseInfo?.finalPrice || 0,
          detail_data: detailData  // 存储完整商品详情 (统一用下划线命名)
        }
      } else {
        delete favorites[key]
      }
      localStorage.setItem(localStorageKey, JSON.stringify(favorites))
      return true
    } catch (e) {
      console.error('[favoriteManager] updateLocalStorage 失败:', e)
      return false
    }
  },

  // 更新内存中的收藏状态缓存（用于列表页面批量同步）
  updateCache(productId, platform, isFavorite, baseInfo = null, detailData = null) {
    const normalizedPlatform = this.normalizePlatform(platform)
    const key = `${normalizedPlatform}_${productId}`
    // console.log('[favoriteManager] updateCache:', { productId, platform, normalizedPlatform, key, isFavorite })
    if (isFavorite) {
      const item = this.getItemInfo(productId, platform)
      cache[key] = {
        ...item,
        platform,
        productId,
        goods_name: baseInfo?.goods_name || item?.goods_name || '',
        goods_img: baseInfo?.goods_img || item?.goods_img || '',
        price: baseInfo?.price || baseInfo?.finalPrice || item?.price || 0,
        detail_data: detailData || item?.detail_data  // 存储完整商品详情 (统一用下划线命名)
      }
      // console.log('[favoriteManager] 缓存已更新, key:', key, '缓存大小:', Object.keys(cache).length)
    } else {
      delete cache[key]
      // console.log('[favoriteManager] 缓存已删除, key:', key)
    }
  },

  // 标准化平台key（前端UI key -> 后端 data key）
  normalizePlatform(platform) {
    // 前端UI平台key映射
    const platformMap = {
      'jdd': 'jd',
      'tmm': 'tm',
      'tbb': 'tb',
      'wpp': 'wpp',
      'dyy': 'dyy',
      'pdd': 'pdd',
      // 兼容旧的平台名称
      'JdPlatform': 'jd',
      'WppPlatform': 'wpp',
      'PddPlatform': 'pdd',
      'DyyPlatform': 'dyy',
      // 兼容天猫平台标识
      'tmall': 'tm',
      'taobao': 'tb',
      'tb': 'tb',
      'tm': 'tm'
    }
    return platformMap[platform] || platform
  },

  // 从商品信息中提取 SKU ID（根据不同平台调整）
  extractSkuId(productInfo, platform) {
    if (!productInfo) return null

    const fieldMap = getPlatformFieldMap(platform)
    if (!fieldMap) {
      console.warn('[favoriteManager] extractSkuId: 未找到平台映射', platform)
      return null
    }

    // 优先使用 fieldMap 中定义的字段
    const skuIdFields = ['skuId', 'id', 'num_iid', 'goodsId', 'sourceId']
    for (const field of skuIdFields) {
      const value = productInfo[field]
      if (value && typeof value === 'string' && value.trim()) {
        // 排除明显错误的值（如 "undefined"）
        if (value !== 'undefined' && value !== 'null') {
          return value
        }
      }
      if (value && typeof value === 'number') {
        return String(value)
      }
    }

    // 如果没有找到，返回 null 而不是 undefined
    return null
  },

  // 格式化商品信息（用于存储和传输）
  formatProductInfo(productInfo, platform, fieldMap) {
    if (!productInfo) return {}

    // 如果传入了 fieldMap，使用 fieldMap 中定义的字段
    if (fieldMap) {
      const result = {}
      if (fieldMap.title && productInfo[fieldMap.title]) result.goods_name = productInfo[fieldMap.title]
      if (fieldMap.image && productInfo[fieldMap.image]) result.goods_img = productInfo[fieldMap.image]
      if (fieldMap.price && productInfo[fieldMap.price]) result.price = Number(productInfo[fieldMap.price]) || 0
      if (fieldMap.originalPrice && productInfo[fieldMap.originalPrice]) result.original_price = Number(productInfo[fieldMap.originalPrice]) || 0
      if (fieldMap.salesCount && productInfo[fieldMap.salesCount]) result.sales_count = productInfo[fieldMap.salesCount]
      if (fieldMap.couponUrl && productInfo[fieldMap.couponUrl]) result.coupon_url = productInfo[fieldMap.couponUrl]
      if (fieldMap.clickUrl && productInfo[fieldMap.clickUrl]) result.click_url = productInfo[fieldMap.clickUrl]
      // 添加更多字段支持
      if (productInfo.mainImage) result.mainImage = productInfo.mainImage
      if (productInfo.images) result.images = productInfo.images
      if (productInfo.goodsCarouselPictures) result.goodsCarouselPictures = productInfo.goodsCarouselPictures
      if (productInfo.goodsDetailPictures) result.goodsDetailPictures = productInfo.goodsDetailPictures
      if (productInfo.whiteImage) result.whiteImage = productInfo.whiteImage
      if (productInfo.brandName) result.brandName = productInfo.brandName
      if (productInfo.goodsPromotionInfo) result.goodsPromotionInfo = productInfo.goodsPromotionInfo
      if (productInfo.storeServiceCapability) result.storeServiceCapability = productInfo.storeServiceCapability
      if (productInfo.unifiedTags) result.unifiedTags = productInfo.unifiedTags
      if (productInfo.lowPriceTag) result.lowPriceTag = productInfo.lowPriceTag
      if (productInfo.actTips) result.actTips = productInfo.actTips
      if (productInfo.coupon) result.coupon = productInfo.coupon
      return result
    }

    // 默认处理逻辑
    return {
      goods_name: productInfo.goods_name || productInfo.title || productInfo.goodsName || '',
      goods_img: productInfo.goods_img || productInfo.image || productInfo.pictUrl || productInfo.mainImage || '',
      price: productInfo.price || productInfo.finalPrice || productInfo.salesCount || 0,
      original_price: productInfo.original_price || productInfo.originalPrice || 0,
      sales_count: productInfo.sales_count || productInfo.sales || 0,
      coupon_url: productInfo.coupon_url || productInfo.couponUrl || '',
      click_url: productInfo.click_url || productInfo.clickUrl || '',
      // 添加更多字段支持
      mainImage: productInfo.mainImage || '',
      images: productInfo.images || [],
      goodsCarouselPictures: productInfo.goodsCarouselPictures || [],
      goodsDetailPictures: productInfo.goodsDetailPictures || [],
      whiteImage: productInfo.whiteImage || '',
      brandName: productInfo.brandName || '',
      goodsPromotionInfo: productInfo.goodsPromotionInfo || '',
      storeServiceCapability: productInfo.storeServiceCapability || '',
      unifiedTags: productInfo.unifiedTags || [],
      lowPriceTag: productInfo.lowPriceTag || '',
      actTips: productInfo.actTips || '',
      coupon: productInfo.coupon || null
    }
  },

  // 根据 productInfo 获取 productId（兼容多种数据结构）
  getProductId(productInfo, platform) {
    // 1. 直接有 id 字段
    if (productInfo.id) return productInfo.id

    // 2. 使用 extractSkuId 提取
    const skuId = this.extractSkuId(productInfo, platform)
    if (skuId) return skuId

    // 3. 根据平台特定字段获取
    if ((platform === 'jdd' || platform === 'jd') && productInfo.skuId) return productInfo.skuId
    if ((platform === 'tmm' || platform === 'tm') && productInfo.num_iid) return productInfo.num_iid
    if ((platform === 'tbb' || platform === 'tb') && productInfo.num_iid) return productInfo.num_iid
    if ((platform === 'pdd') && (productInfo.goods_sign || productInfo.goodsId)) return productInfo.goods_sign || productInfo.goodsId
    if ((platform === 'wpp') && (productInfo.sourceId || productInfo.goodsId)) return productInfo.sourceId || productInfo.goodsId

    // 4. 兜底：使用 title 生成一个稳定的 ID（仅用于本地存储）
    if (productInfo.title) {
      console.warn('[favoriteManager] getProductId: 无法获取商品ID，使用 title 生成临时ID')
      return `temp_${productInfo.title.substring(0, 20)}_${productInfo.price || 0}`
    }

    return null
  },

  // 根据 productInfo 获取 clickUrl（仅京东需要从链接提取）
  getClickUrl(productInfo, platform) {
    if (!productInfo) return ''
    if (platform === 'jdd' || platform === 'jd') {
      // 京东从 shortCommissionLink 中提取
      const shortLink = productInfo.shortCommissionLink || productInfo.clickUrl || ''
      return shortLink
    }
    return productInfo.click_url || productInfo.clickUrl || ''
  }
}

export default favoriteManager
